import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { push, replace } from "connected-react-router";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import APPCONFIG from "constants/Config";
import {
  LOGIN_LINK_PAGE_PATH,
  LOGIN_LINK_CUSTOMER_APP_PAGE_PATH,
  LOGIN_LINK_CODE_PAGE_PATH
} from "constants/Paths";
import {
  LOGIN_LINK_TTL_MS,
  LOGIN_LINK_STATUS_EXPIRED,
  LOGIN_LINK_STATUS_OPENED_ELSEWHERE,
  hashBindingSecret,
  loginLinkStatus,
  readBindingSecret,
  readFreshPending,
  readPending,
  removePending,
  savePending
} from "services/loginLinkService";

const CONFIRM_EMAIL_PATH = "/confirm-email";

const isLoginLinkPage = pathname =>
  pathname.startsWith(`${LOGIN_LINK_PAGE_PATH}/`) ||
  pathname.startsWith(`${LOGIN_LINK_CUSTOMER_APP_PAGE_PATH}/`);

class LoginLinkWatcher extends React.Component {
  constructor(props, context) {
    super(props, context);

    this._isMounted = false;
    this._watching = false;
    this._generation = 0;
    this._echo = null;
    this._expiryTimer = null;
    this._expirySentAt = null;
  }

  componentDidMount() {
    this._isMounted = true;
    this.sync();
  }

  componentDidUpdate() {
    this.sync();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.stop();
  }

  sync = () => {
    const pending = readFreshPending();

    if (
      this.props.isAuthenticated ||
      isLoginLinkPage(this.props.pathname) ||
      !pending ||
      !readBindingSecret()
    ) {
      this.stop();
      return;
    }

    this.start();
    this.scheduleExpiry(pending);
  };

  scheduleExpiry(pending) {
    if (this._expirySentAt === pending.sent_at) {
      return;
    }

    clearTimeout(this._expiryTimer);
    this._expirySentAt = pending.sent_at;
    this._expiryTimer = setTimeout(
      this.sync,
      Math.max(pending.sent_at + LOGIN_LINK_TTL_MS - Date.now(), 0) + 1000
    );
  }

  start() {
    if (this._watching) {
      return;
    }

    this._watching = true;
    const generation = ++this._generation;

    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    hashBindingSecret(readBindingSecret()).then(hash => {
      if (!hash || !this._watching || generation !== this._generation) {
        return;
      }

      try {
        this._echo = new Echo({
          broadcaster: "pusher",
          key: APPCONFIG.pusher_key,
          cluster: APPCONFIG.pusher_cluster,
          forceTLS: true,
          Pusher
        });

        this._echo
          .channel(`login-link-${hash}`)
          .subscribed(this.checkStatus)
          .listen(".opened", this.checkStatus);
      } catch (e) {
        this._echo = null;
      }
    });
  }

  stop() {
    clearTimeout(this._expiryTimer);
    this._expiryTimer = null;
    this._expirySentAt = null;

    if (!this._watching) {
      return;
    }

    this._watching = false;
    this._generation += 1;

    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    if (this._echo) {
      const echo = this._echo;
      this._echo = null;
      echo.disconnect();
    }
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      this.checkStatus();
    }
  };

  checkStatus = () => {
    const pending = readFreshPending();
    const bindingSecret = readBindingSecret();

    if (!this._watching || !pending || !bindingSecret) {
      return;
    }

    const sentAt = pending.sent_at;

    this.props
      .dispatch(
        loginLinkStatus({ email: pending.email, binding_secret: bindingSecret })
      )
      .then(
        data => this.handleStatus(sentAt, data ? data.status : null),
        () => {}
      );
  };

  handleStatus(sentAt, status) {
    const pending = readPending();

    if (
      !this._isMounted ||
      !pending ||
      pending.sent_at !== sentAt ||
      this.props.isAuthenticated
    ) {
      return;
    }

    if (status === LOGIN_LINK_STATUS_EXPIRED) {
      removePending();
      this.sync();
      return;
    }

    if (
      status !== LOGIN_LINK_STATUS_OPENED_ELSEWHERE ||
      document.visibilityState !== "visible" ||
      pending.code_opened ||
      isLoginLinkPage(this.props.pathname)
    ) {
      return;
    }

    savePending({ ...pending, code_opened: true });

    const navigate =
      this.props.pathname === CONFIRM_EMAIL_PATH ? replace : push;
    this.props.dispatch(navigate(LOGIN_LINK_CODE_PAGE_PATH));
  }

  render() {
    return null;
  }
}

LoginLinkWatcher.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  pathname: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  pathname: state.router.location.pathname
});

export default connect(mapStateToProps)(LoginLinkWatcher);
